import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import { useTranslation } from 'react-i18next'

import { opsApi } from '../../api/endpoints'
import { useFormatters } from '../../hooks/useFormatters'
import { ErrorState } from '../../components/common/States'
import type { ServiceItemDto, ServiceItemRequest, ServiceUnit } from '../../types/api'

const UNITS: ServiceUnit[] = ['PER_ITEM', 'PER_PERSON', 'PER_NIGHT', 'PER_STAY']

/** The public menu already names these; the console says the same words, not synonyms. */
const UNIT_KEY: Record<ServiceUnit, string> = {
  PER_ITEM: 'services.perItem',
  PER_PERSON: 'services.perPerson',
  PER_NIGHT: 'services.perNight',
  PER_STAY: 'services.perStay',
}

/**
 * The menu and its prices.
 *
 * The list is fetched in English on purpose. The public endpoints resolve a name through the
 * translation chain, so asking for Finnish here and saving would write the Finnish text into
 * the base column that every other language falls back to. English is the base, so what the
 * owner edits is what is actually stored. Translations are left alone by the API when the
 * request omits them, which is what this form does.
 */
export function AdminServicesPage() {
  const { t } = useTranslation()
  const { moneyPrecise } = useFormatters()
  const queryClient = useQueryClient()

  const [editing, setEditing] = useState<ServiceItemDto | null>(null)
  const [form, setForm] = useState<ServiceItemRequest | null>(null)

  const services = useQuery({
    queryKey: ['admin', 'ops', 'services'],
    queryFn: () => opsApi.services('en'),
  })

  const save = useMutation({
    mutationFn: () => opsApi.updateService(editing!.id, form!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ops', 'services'] })
      void queryClient.invalidateQueries({ queryKey: ['services'] })
      setEditing(null)
      setForm(null)
    },
  })

  const openEditor = (item: ServiceItemDto) => {
    setEditing(item)
    setForm({
      code: item.code,
      name: item.name,
      summary: item.summary ?? null,
      description: item.description ?? null,
      category: item.category,
      price: item.price ?? null,
      priceConfirmed: item.priceConfirmed,
      unit: item.unit,
      icon: item.icon ?? null,
      imageUrl: item.imageUrl ?? null,
      advanceNoticeMinutes: item.advanceNoticeMinutes,
      availableFrom: item.availableFrom ?? null,
      availableTo: item.availableTo ?? null,
      maxQuantityPerOrder: item.maxQuantityPerOrder,
      orderableOnline: item.orderableOnline,
      displayOrder: item.displayOrder,
      active: item.active,
    })
  }

  const patch = (changes: Partial<ServiceItemRequest>) =>
    setForm((current) => (current ? { ...current, ...changes } : current))

  // "17:00:00" from the API, "17:00" in the input. A time input rejects the seconds.
  const toInput = (value?: string | null) => (value ? value.slice(0, 5) : '')
  const fromInput = (value: string) => (value ? `${value}:00` : null)

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('admin.services')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.servicesIntro')}
      </Typography>

      {services.isError && <ErrorState error={services.error} onRetry={() => void services.refetch()} />}

      <Stack spacing={1.5}>
        {(services.data ?? []).map((item) => (
          <Paper
            key={item.id}
            elevation={0}
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              opacity: item.active ? 1 : 0.55,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box aria-hidden sx={{ fontSize: '1.5rem' }}>
                  {item.icon ?? '•'}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(`services.${item.category}`)} · {t(UNIT_KEY[item.unit])}
                    {item.availableFrom && item.availableTo
                      ? ` · ${toInput(item.availableFrom)}–${toInput(item.availableTo)}`
                      : ''}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                {!item.active && <Chip size="small" label={t('admin.hidden')} />}
                {!item.orderableOnline && item.active && (
                  <Chip size="small" variant="outlined" label={t('admin.notOrderable')} />
                )}
                <Typography variant="body1" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {item.priceConfirmed && item.price != null
                    ? moneyPrecise(item.price)
                    : t('services.askUs')}
                </Typography>
                <Button size="small" startIcon={<EditIcon />} onClick={() => openEditor(item)}>
                  {t('common.edit')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog
        open={editing !== null && form !== null}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editing?.name}</DialogTitle>
        <DialogContent>
          {save.isError && <ErrorState error={save.error} />}

          {form && (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('admin.price')}
                  type="number"
                  value={form.price ?? ''}
                  onChange={(event) =>
                    patch({ price: event.target.value === '' ? null : Number(event.target.value) })
                  }
                  disabled={!form.priceConfirmed}
                  slotProps={{
                    input: { endAdornment: <InputAdornment position="end">€</InputAdornment> },
                  }}
                  sx={{ maxWidth: { sm: 160 } }}
                />
                <TextField
                  select
                  label={t('admin.pricedPer')}
                  value={form.unit}
                  onChange={(event) => patch({ unit: event.target.value as ServiceUnit })}
                  fullWidth
                >
                  {UNITS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {t(UNIT_KEY[unit])}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {/* Breakfast has no price yet. Publishing 0.00 would be a lie; "ask us" is not. */}
              <FormControlLabel
                control={
                  <Switch
                    checked={!form.priceConfirmed}
                    onChange={(event) =>
                      patch({
                        priceConfirmed: !event.target.checked,
                        price: event.target.checked ? null : form.price,
                      })
                    }
                  />
                }
                label={t('admin.priceNotSet')}
              />

              <Divider />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('admin.servedFrom')}
                  type="time"
                  value={toInput(form.availableFrom)}
                  onChange={(event) => patch({ availableFrom: fromInput(event.target.value) })}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
                <TextField
                  label={t('admin.servedTo')}
                  type="time"
                  value={toInput(form.availableTo)}
                  onChange={(event) => patch({ availableTo: fromInput(event.target.value) })}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('admin.noticeMinutes')}
                  type="number"
                  value={form.advanceNoticeMinutes}
                  onChange={(event) => patch({ advanceNoticeMinutes: Number(event.target.value) })}
                  helperText={t('admin.noticeHelp')}
                  fullWidth
                />
                <TextField
                  label={t('admin.maxPerOrder')}
                  type="number"
                  value={form.maxQuantityPerOrder}
                  onChange={(event) => patch({ maxQuantityPerOrder: Number(event.target.value) })}
                  fullWidth
                />
              </Stack>

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.orderableOnline}
                    onChange={(event) => patch({ orderableOnline: event.target.checked })}
                  />
                }
                label={t('admin.orderableOnline')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.active}
                    onChange={(event) => patch({ active: event.target.checked })}
                  />
                }
                label={t('admin.showOnSite')}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>{t('common.back')}</Button>
          <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
